import { z } from 'zod';

// Wallet contract for 0xProcessing withdrawals. Types are inferred from these schemas so the
// runtime check at the network boundary and the compile-time shape cannot drift apart.
const decimal = z.string().regex(/^(0|[1-9]\d{0,29})(\.\d{1,18})?$/);

export const withdrawalStateSchema = z.enum([
  'awaiting_approval',
  'submitting',
  'pending',
  'unknown',
  'review',
  'confirmed',
  'aborted',
  'rejected',
]);

export const withdrawalCurrenciesSchema = z.object({
  currencies: z.array(
    z.object({
      id: z.string().min(1),
      label: z.string().min(1),
      requiresTag: z.boolean(),
      minimumWithdrawalAmount: decimal,
      estimatedNetworkFee: decimal,
    })
  ),
});

export const withdrawalPreviewSchema = z.object({
  debit: decimal,
  accountCurrency: z.string().regex(/^[A-Z]{3}$/),
  currency: z.string().min(1),
  assetAmount: decimal,
  estimatedNetworkFee: decimal,
  estimatedReceived: decimal,
  address: z.string().min(1),
  tag: z.string().nullable(),
  confirmationToken: z.string().min(1),
  expiresAt: z.iso.datetime({ offset: true }),
});

export const withdrawalResultSchema = z.object({
  transactionId: z.uuid(),
  status: withdrawalStateSchema,
  debit: decimal,
  accountCurrency: z.string(),
  assetAmount: decimal,
  estimatedNetworkFee: decimal,
  estimatedReceived: decimal,
  failureReason: z.string().nullable(),
});

export const cryptoWithdrawalMetadataSchema = z.object({
  provider: z.literal('zero_x_processing'),
  state: withdrawalStateSchema,
  accountCurrency: z.string(),
  debit: z.string(),
  assetCurrency: z.string(),
  assetAmount: z.string(),
  estimatedNetworkFee: z.string(),
  estimatedReceived: z.string(),
  address: z.string(),
  tag: z.string().nullable(),
  providerWithdrawalId: z.string().optional(),
  actualFee: z.string().nullable().optional(),
  hash: z.string().nullable().optional(),
  failureReason: z.string().nullable().optional(),
});

export type ZeroXWithdrawalState = z.infer<typeof withdrawalStateSchema>;

export type ZeroXWithdrawalCurrency = z.infer<typeof withdrawalCurrenciesSchema>['currencies'][number];

export type ZeroXWithdrawalPreview = z.infer<typeof withdrawalPreviewSchema>;

export type ZeroXWithdrawalResult = z.infer<typeof withdrawalResultSchema>;

export type ZeroXWithdrawalMetadata = z.infer<typeof cryptoWithdrawalMetadataSchema>;

export type ZeroXWithdrawalInput = { amount: string; currency: string; address: string; tag?: string };
